import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as moment from 'moment';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-banner-edit',
  templateUrl: './banner-edit.component.html',
  styleUrls: ['./banner-edit.component.scss'],
  standalone: false
})
export class BannerEditComponent implements OnInit {
  bannerForm: FormGroup;
  selectedFile: File | null = null;
  previewUrl: string | null = null;
  isNew = false;
  originalFileName: string = '';
  uploadFilePath = environment.assetsUrl;
  
  minFromDate!: Date;
  minToDate!: Date;
  maxDate!: Date;

  constructor(
    private formBuilder: FormBuilder,
    private snackBarService: SnackBarService,
    private dialogRef: MatDialogRef<BannerEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isNew = data.isNew || false;
    this.setupDateRestrictions();
    this.bannerForm = this.createForm();
  }

  ngOnInit(): void {
    if (this.data.banner) {
      this.populateForm(this.data.banner);
    }
    
    this.bannerForm.get('fromDate')?.valueChanges.subscribe(fromDate => {
      this.updateToDateRestrictions(fromDate);
    });
  }

  private setupDateRestrictions(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    this.minFromDate = today;
    this.minToDate = today;
    
    this.maxDate = new Date();
    this.maxDate.setFullYear(this.maxDate.getFullYear() + 2);
  }

  private updateToDateRestrictions(fromDate: Date | null): void {
    if (fromDate) {
      const nextDay = new Date(fromDate);
      nextDay.setDate(nextDay.getDate() + 1);
      this.minToDate = nextDay;
      
      const currentToDate = this.bannerForm.get('toDate')?.value;
      if (currentToDate && currentToDate <= fromDate) {
        this.bannerForm.get('toDate')?.setValue(null);
      }
    } else {
      this.minToDate = this.minFromDate;
    }
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      fromDate: [null, [this.fromDateValidator.bind(this)]],
      toDate: [null, [this.toDateValidator.bind(this)]]
    }, {
      validators: this.dateRangeValidator.bind(this)
    });
  }

  private fromDateValidator(control: any) {
    if (!control.value) return null;
    
    const selectedDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return { pastDate: true };
    }
    
    return null;
  }

  private toDateValidator(control: any) {
    if (!control.value) return null;
    
    const selectedDate = new Date(control.value);
    const fromDate = this.bannerForm?.get('fromDate')?.value;
    
    if (fromDate) {
      const fromDateTime = new Date(fromDate);
      if (selectedDate <= fromDateTime) {
        return { beforeFromDate: true };
      }
    }
    
    return null;
  }

  private dateRangeValidator(group: FormGroup): any {
    const fromDate = group.get('fromDate')?.value;
    const toDate = group.get('toDate')?.value;
    
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      
      if (to <= from) {
        return { dateRange: true };
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (from < today) {
        return { pastFromDate: true };
      }
    }
    
    return null;
  }

  fromDateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return date >= today;
  }

  toDateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    
    const fromDate = this.bannerForm?.get('fromDate')?.value;
    
    if (fromDate) {
      const fromDateTime = new Date(fromDate);
      fromDateTime.setHours(0, 0, 0, 0);
      
      return date > fromDateTime;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  }

  private validateDates(): string[] {
    // Only validate dates if adding (isNew)
    if (!this.isNew) return [];
    const errors: string[] = [];
    const fromDate = this.bannerForm.get('fromDate')?.value;
    const toDate = this.bannerForm.get('toDate')?.value;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (fromDate) {
      const startDate = new Date(fromDate);
      if (isNaN(startDate.getTime())) {
        errors.push('Invalid start date format.');
      } else if (startDate < today) {
        errors.push('Start date cannot be in the past.');
      }
    }
    if (toDate) {
      const endDate = new Date(toDate);
      if (isNaN(endDate.getTime())) {
        errors.push('Invalid end date format.');
      }
    }
    if (fromDate && toDate) {
      const startDate = new Date(fromDate);
      const endDate = new Date(toDate);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        if (endDate <= startDate) {
          errors.push('End date must be after start date.');
        }
      }
    }
    return errors;
  }

  getErrorMessage(fieldName: string): string {
    const field = this.bannerForm.get(fieldName);
    
    if (fieldName === 'fromDate') {
      if (field?.hasError('required')) return 'Start date is required';
      if (field?.hasError('pastDate')) return 'Start date cannot be in the past';
    }
    
    if (fieldName === 'toDate') {
      if (field?.hasError('required')) return 'End date is required';
      if (field?.hasError('beforeFromDate')) return 'End date must be after start date';
    }
    
    if (field?.hasError('dateRange')) {
      return 'End date must be after start date';
    }
    
    if (field?.hasError('pastFromDate')) {
      return 'Start date cannot be in the past';
    }
    
    return '';
  }

  onSave(): void {
    const dateErrors = this.validateDates();
    
    if (dateErrors.length > 0) {
      this.snackBarService.openSnackBar({
        type: 'error',
        title: 'Invalid Dates',
        message: dateErrors.join(' ')
      });
      
      return;
    }
    
    if (this.data.isNew && !this.selectedFile) {
      this.snackBarService.openSnackBar({
        type: 'error',
        title: 'Validation Error',
        message: 'Image is required for new banners.'
      });
      
      return;
    }
    
    if (this.selectedFile) {
      const fileError = this.validateFile();
      if (fileError) {
        this.snackBarService.openSnackBar({
          type: 'error',
          title: 'Invalid File',
          message: fileError
        });
        
        return;
      }
    }
    
    const formData = {
      file: this.selectedFile,
      fromDate: this.bannerForm.get('fromDate')?.value,
      toDate: this.bannerForm.get('toDate')?.value,
      fileName: this.selectedFile?.name || this.data.banner.fileName
    };
    
    this.dialogRef.close({
      action: 'save',
      data: formData
    });
  }

  private populateForm(banner: any): void {
    this.bannerForm.patchValue({
      fromDate: banner.fromDate ? new Date(banner.fromDate) : null,
      toDate: banner.toDate ? new Date(banner.toDate) : null
    });
    
    if (banner.imageUrl) {
      const cacheBuster = new Date().getTime();
      this.previewUrl = `${this.uploadFilePath}images/banner/${banner.fileName}?cb=${cacheBuster}`;
    }
    
    this.originalFileName = banner.fileName || '';
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    
    if (file) {
      const validationError = this.validateFile();
      if (validationError) {
        this.snackBarService.openSnackBar({
          type: 'error',
          title: 'Invalid File',
          message: validationError
        });
        
        event.target.value = '';
        return;
      }
      
      this.selectedFile = file;
      
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.previewUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      this.selectedFile = null;
      
      if (!this.isNew && this.data.banner?.imageUrl) {
        const cacheBuster = new Date().getTime();
        this.previewUrl = `${this.uploadFilePath}images/banner/${this.data.banner.fileName}?cb=${cacheBuster}`;
      } else {
        this.previewUrl = null;
      }
    }
  }

  removeImage(): void {
    this.selectedFile = null;
    
    if (!this.isNew && this.data.banner?.imageUrl) {
      const cacheBuster = new Date().getTime();
      this.previewUrl = `${this.uploadFilePath}images/banner/${this.data.banner.fileName}?cb=${cacheBuster}`;
    } else {
      this.previewUrl = null;
    }
  }

  get hasImageError(): boolean {
    return this.isNew && !this.selectedFile && !this.previewUrl;
  }

  get isFormValid(): boolean {
    const dateErrors = this.isNew ? this.validateDates() : [];
    const hasImageError = this.isNew && !this.selectedFile;
    const hasFileError = this.selectedFile && this.validateFile() !== null;
    return dateErrors.length === 0 && !hasImageError && !hasFileError;
  }

  private validateFile(): string | null {
    if (!this.selectedFile) return null;
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(this.selectedFile.type.toLowerCase())) {
      return 'Only JPG and PNG files are allowed.';
    }
    
    const maxSize = 2 * 1024 * 1024;
    if (this.selectedFile.size > maxSize) {
      const sizeMB = (this.selectedFile.size / (1024 * 1024)).toFixed(2);
      return `File size (${sizeMB}MB) exceeds the 2MB limit.`;
    }
    
    if (this.selectedFile.name.length > 100) {
      return 'File name is too long (maximum 100 characters).';
    }
    
    return null;
  }

  hasChanges(): boolean {
    const getTimeOrNull = (d: any) => {
      if (d instanceof Date && !isNaN(d.getTime())) return d.getTime();
      if (typeof d === 'string' || typeof d === 'number') {
        const dateObj = new Date(d);
        return !isNaN(dateObj.getTime()) ? dateObj.getTime() : null;
      }
      return null;
    };
    if (this.isNew) {
      return this.selectedFile !== null || 
             this.bannerForm.get('fromDate')?.value !== null || 
             this.bannerForm.get('toDate')?.value !== null;
    } else {
      const currentFromDate = this.bannerForm.get('fromDate')?.value;
      const currentToDate = this.bannerForm.get('toDate')?.value;
      const originalFromDate = this.data.banner?.fromDate ? new Date(this.data.banner.fromDate) : null;
      const originalToDate = this.data.banner?.toDate ? new Date(this.data.banner.toDate) : null;
      const dateChanged = 
        getTimeOrNull(currentFromDate) !== getTimeOrNull(originalFromDate) ||
        getTimeOrNull(currentToDate) !== getTimeOrNull(originalToDate);
      return this.selectedFile !== null || dateChanged;
    }
  }

  onCancel(): void {
    if (this.hasChanges()) {
      const confirmed = confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmed) {
        return;
      }
    }
    
    this.dialogRef.close({
      action: 'cancel'
    });
  }

  onDelete(): void {
    if (!this.isNew) {
      const confirmed = confirm('Are you sure you want to delete this banner? This action cannot be undone.');
      if (confirmed) {
        this.dialogRef.close({
          action: 'delete',
          data: { index: this.data.index }
        });
      }
    }
  }

  getImageUrl(): string | null {
    if (this.previewUrl) {
      return this.previewUrl;
    }
    
    if (!this.isNew && this.data.banner?.fileName) {
      const cacheBuster = new Date().getTime();
      return `${this.uploadFilePath}images/banner/${this.data.banner.fileName}?cb=${cacheBuster}`;
    }
    
    return null;
  }
}
